import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../context/AlertContext';
import api from '../services/api';
import { ArrowLeft, Save, Upload, X, Plus, Trash2, Edit2 } from 'lucide-react';
import { Form, Button, Card, Container, Row, Col, Image, ListGroup, Table, Badge } from 'react-bootstrap';
import './ClientForm.css';
import AppointmentModal from './AppointmentModal';
import { formatPhoneBrazilian, unformatPhone, toUpperCase, toLowerCase } from '../utils/formatters';


const ClientForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showAlert } = useAlert();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        observation: ''
    });
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [currentPhoto, setCurrentPhoto] = useState<string | null>(null);
    const [removePhotoFlag, setRemovePhotoFlag] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dependents, setDependents] = useState<Array<{ id?: number; name: string; isNew?: boolean }>>([]);
    const [newDependentName, setNewDependentName] = useState('');
    const [editingDependentId, setEditingDependentId] = useState<number | null>(null); // Index in array
    const [editingDependentName, setEditingDependentName] = useState('');

    // Appointments
    const [appointments, setAppointments] = useState<any[]>([]);
    const [appointmentFilter, setAppointmentFilter] = useState('ALL'); // ALL, PENDING, CONFIRMED, COMPLETED, CANCELLED
    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const [selectedAppointmentId, setSelectedAppointmentId] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (id) {
            fetchClient();
            fetchAppointments();
        }
    }, [id]);

    const fetchAppointments = async () => {
        try {
            const res = await api.get('/appointments/', {
                params: { client: id }
            });
            setAppointments(res.data);
        } catch (error) {
            console.error('Error fetching appointments:', error);
        }
    };

    const fetchClient = async () => {
        try {
            const response = await api.get(`/clients/${id}/`);
            setFormData({
                name: response.data.name,
                phone: response.data.phone,
                email: response.data.email || '',
                observation: response.data.observation || ''
            });
            if (response.data.photo) {
                setCurrentPhoto(response.data.photo);
            }
            if (response.data.dependents) {
                setDependents(response.data.dependents);
            }
        } catch (error) {
            console.error('Error fetching client:', error);
        }
    };

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setPhotoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const removePhoto = () => {
        setPhotoFile(null);
        setPhotoPreview(null);
        if (currentPhoto) {
            setRemovePhotoFlag(true);
            setCurrentPhoto(null);
        }
    };

    const addDependent = () => {
        if (!newDependentName.trim()) {
            showAlert('Por favor, informe o nome do dependente.', { type: 'warning', title: 'Campo obrigatório' });
            return;
        }
        setDependents([...dependents, { name: newDependentName.trim(), isNew: true }]);
        setNewDependentName('');
    };

    const removeDependent = (index: number) => {
        const dependent = dependents[index];

        showAlert('Tem certeza que deseja remover este dependente?', {
            title: 'Confirmação',
            confirmable: true,
            confirmText: 'Remover',
            confirmVariant: 'btn-primary',
            onConfirm: async () => {
                // If it's an existing dependent (has an id), delete it from the backend
                if (dependent.id && id) {
                    try {
                        await api.delete(`/dependents/${dependent.id}/`);
                    } catch (error) {
                        console.error('Error deleting dependent:', error);
                        return;
                    }
                }

                // Remove from local state
                setDependents(dependents.filter((_, i) => i !== index));
            }
        });
    };

    const startEditingDependent = (index: number) => {
        setEditingDependentId(index);
        setEditingDependentName(dependents[index].name);
    };

    const saveDependentEdit = (index: number) => {
        if (editingDependentName.trim()) {
            const newDependents = [...dependents];
            newDependents[index].name = editingDependentName;
            setDependents(newDependents);
            setEditingDependentId(null);
            setEditingDependentName('');
        }
    };

    const cancelDependentEdit = () => {
        setEditingDependentId(null);
        setEditingDependentName('');
    };

    const getFieldLabel = (field: string): string => {
        const fieldLabels: Record<string, string> = {
            name: 'Nome',
            phone: 'Telefone',
            email: 'Email',
            observation: 'Observação',
            photo: 'Foto',
            non_field_errors: 'Erro'
        };
        return fieldLabels[field] || field;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const submitData = new FormData();
            submitData.append('name', formData.name);
            submitData.append('phone', formData.phone);
            submitData.append('email', formData.email.toLowerCase());
            submitData.append('observation', formData.observation);

            if (photoFile) {
                submitData.append('photo', photoFile);
            } else if (removePhotoFlag) {
                submitData.append('photo', '');
            }

            let clientId = id;

            if (id) {
                await api.put(`/clients/${id}/`, submitData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            } else {
                const response = await api.post('/clients/', submitData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                clientId = response.data.id;
            }

            // Save new or updated dependents
            for (const dependent of dependents) {
                if (dependent.isNew && clientId) {
                    await api.post('/dependents/', {
                        client: clientId,
                        name: dependent.name
                    });
                } else if (dependent.id) {
                    // Update existing dependent
                    await api.put(`/dependents/${dependent.id}/`, {
                        client: clientId,
                        name: dependent.name
                    });
                }
            }

            showAlert('Cliente salvo com sucesso!', { type: 'success' });
            navigate('/clients');
        } catch (err: any) {
            console.error('Error saving client:', err);
            
            let errorMsg = 'Erro ao salvar cliente. Tente novamente.';

            if (err.response?.data) {
                const data = err.response.data;
                
                // Caso 1: Erro genérico com chave "error" ou "detail"
                if (data.error) {
                    errorMsg = data.error;
                } else if (data.detail) {
                    errorMsg = data.detail;
                }
                // Caso 2: Erros de validação (ex: 400 Bad Request)
                else if (typeof data === 'object') {
                    const errorMessages: string[] = [];
                    
                    for (const field of Object.keys(data)) {
                        const fieldErrors = data[field];
                        const fieldLabel = getFieldLabel(field);
                        
                        if (Array.isArray(fieldErrors)) {
                            errorMessages.push(`${fieldLabel}: ${fieldErrors.join(', ')}`);
                        } else if (typeof fieldErrors === 'string') {
                            errorMessages.push(`${fieldLabel}: ${fieldErrors}`);
                        }
                    }
                    
                    if (errorMessages.length > 0) {
                        errorMsg = errorMessages.join('\n');
                    }
                }
            }

            showAlert(errorMsg, { type: 'error', title: 'Erro ao Salvar Cliente' });
        } finally {
            setLoading(false);
        }
    };

    const displayPhoto = photoPreview || (currentPhoto ? `${currentPhoto}` : null);

    const filteredAppointments = appointments.filter(appt => {
        if (appointmentFilter === 'ALL') return true;
        return appt.status === appointmentFilter;
    });

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'PENDING': return <Badge bg="warning">Agendado</Badge>;
            case 'CONFIRMED': return <Badge bg="success">Confirmado</Badge>;
            case 'COMPLETED': return <Badge bg="primary">Concluído</Badge>;
            case 'CANCELLED': return <Badge bg="danger">Cancelado</Badge>;
            default: return <Badge bg="secondary">{status}</Badge>;
        }
    };

    const handleAppointmentClick = (id: number) => {
        setSelectedAppointmentId(id);
        setShowAppointmentModal(true);
    };

    const handleAppointmentSuccess = () => {
        fetchAppointments();
        setShowAppointmentModal(false);
    };

    return (
        <Container className="py-2">
            <div className="d-flex align-items-center mb-4">
                <Button
                    variant="link"
                    onClick={() => navigate('/clients')}
                    className="text-decoration-none text-secondary p-0 me-3 d-flex align-items-center gap-2"
                >
                    <ArrowLeft size={20} /> Voltar
                </Button>
                <h2 className="mb-0">{id ? 'Editar Cliente' : 'Novo Cliente'}</h2>
            </div>

            <Card className="shadow-sm border-0">
                <Card.Body className="p-4">
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={8}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nome</Form.Label>
                                            <Form.Control
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: toUpperCase(e.target.value) })}
                                                required
                                                placeholder="Nome completo"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Telefone</Form.Label>
                                            <Form.Control
                                                value={formatPhoneBrazilian(formData.phone)}
                                                onChange={(e) => setFormData({ ...formData, phone: unformatPhone(e.target.value) })}
                                                required
                                                placeholder="(00) 00000-0000"
                                                maxLength={15}
                                            />
                                            <Form.Text className="text-muted">
                                                DDD + 8 ou 9 dígitos (10 ou 11 dígitos no total)
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        placeholder="email@exemplo.com"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label>Observação</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        value={formData.observation}
                                        onChange={(e) => setFormData({ ...formData, observation: toUpperCase(e.target.value) })}
                                        placeholder="Notas e observações sobre o cliente..."
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Dependentes (Filhos)</Form.Label>
                                    <div className="dependent-input-group mb-2">
                                        <Form.Control
                                            value={newDependentName}
                                            onChange={(e) => setNewDependentName(toUpperCase(e.target.value))}
                                            placeholder="Nome do dependente"
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    addDependent();
                                                }
                                            }}
                                        />
                                        <Button
                                            variant="success"
                                            onClick={addDependent}
                                            className="d-flex align-items-center justify-content-center gap-1 dependent-add-btn"
                                        >
                                            <Plus size={18} /> Adicionar
                                        </Button>
                                    </div>
                                    {dependents.length > 0 && (
                                        <ListGroup>
                                            {dependents.map((dependent, index) => (
                                                <ListGroup.Item
                                                    key={index}
                                                    className="d-flex justify-content-between align-items-center"
                                                >
                                                    {editingDependentId === index ? (
                                                        <div className="d-flex gap-2 w-100">
                                                            <Form.Control
                                                                size="sm"
                                                                value={editingDependentName}
                                                                onChange={(e) => setEditingDependentName(toUpperCase(e.target.value))}
                                                            />
                                                            <Button size="sm" variant="success" onClick={() => saveDependentEdit(index)}><Save size={16} /></Button>
                                                            <Button size="sm" variant="secondary" onClick={cancelDependentEdit}><X size={16} /></Button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {dependent.name}
                                                            <div className="d-flex gap-1">
                                                                <Button
                                                                    variant="link"
                                                                    className="text-secondary p-0"
                                                                    onClick={() => startEditingDependent(index)}
                                                                >
                                                                    <Edit2 size={18} />
                                                                </Button>
                                                                <Button
                                                                    variant="link"
                                                                    className="text-secondary p-0"
                                                                    onClick={() => removeDependent(index)}
                                                                >
                                                                    <Trash2 size={18} />
                                                                </Button>
                                                            </div>
                                                        </>
                                                    )}
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Foto do Cliente</Form.Label>
                                    <div className="text-center">
                                        {displayPhoto ? (
                                            <div
                                                className="position-relative d-inline-block"
                                                onClick={() => fileInputRef.current?.click()}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <Image
                                                    src={displayPhoto}
                                                    alt="Preview"
                                                    roundedCircle
                                                    style={{
                                                        width: '150px',
                                                        height: '150px',
                                                        objectFit: 'cover',
                                                        border: '3px solid #dee2e6'
                                                    }}
                                                />
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    className="position-absolute top-0 end-0 rounded-circle p-1"
                                                    onClick={(e: React.MouseEvent) => {
                                                        e.stopPropagation();
                                                        removePhoto();
                                                    }}
                                                    style={{ width: '30px', height: '30px' }}
                                                >
                                                    <X size={16} />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div
                                                className="d-flex align-items-center justify-content-center bg-light rounded-circle mx-auto"
                                                onClick={() => fileInputRef.current?.click()}
                                                style={{
                                                    width: '150px',
                                                    height: '150px',
                                                    border: '2px dashed #dee2e6',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <Upload size={40} className="text-muted" />
                                            </div>
                                        )}
                                        <Form.Control
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handlePhotoChange}
                                            style={{ display: 'none' }}
                                        />
                                        <Form.Text className="text-muted d-block mt-2">
                                            Clique na imagem para alterar • JPG, PNG, GIF
                                        </Form.Text>
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end">
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={loading}
                                className="d-flex align-items-center gap-2 px-4"
                            >
                                <Save size={18} /> Salvar
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            {id && (
                <Card className="shadow-sm border-0 mt-4 mb-4">
                    <Card.Body className="p-4">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
                            <h4 className="mb-0">Histórico de Agendamentos</h4>
                            <Button variant="success" onClick={() => navigate(`/scheduling?client=${id}`)} className="d-flex align-items-center gap-1 w-100 w-md-auto justify-content-center">
                                <Plus size={18} /> Novo Agendamento
                            </Button>
                        </div>
                        <div className="status-filter-group mb-4">
                            <Button 
                                variant={appointmentFilter === 'ALL' ? 'primary' : 'outline-primary'} 
                                onClick={() => setAppointmentFilter('ALL')}
                                className="status-filter-btn"
                            >
                                Todos
                            </Button>
                            <Button 
                                variant={appointmentFilter === 'PENDING' ? 'warning' : 'outline-warning'} 
                                onClick={() => setAppointmentFilter('PENDING')}
                                className="status-filter-btn"
                            >
                                Agendados
                            </Button>
                            <Button 
                                variant={appointmentFilter === 'CONFIRMED' ? 'success' : 'outline-success'} 
                                onClick={() => setAppointmentFilter('CONFIRMED')}
                                className="status-filter-btn"
                            >
                                Confirmados
                            </Button>
                            <Button 
                                variant={appointmentFilter === 'COMPLETED' ? 'info' : 'outline-info'} 
                                onClick={() => setAppointmentFilter('COMPLETED')}
                                className="status-filter-btn"
                            >
                                Concluídos
                            </Button>
                            <Button 
                                variant={appointmentFilter === 'CANCELLED' ? 'danger' : 'outline-danger'} 
                                onClick={() => setAppointmentFilter('CANCELLED')}
                                className="status-filter-btn"
                            >
                                Cancelados
                            </Button>
                        </div>

                        {filteredAppointments.length > 0 ? (
                            <Table hover responsive>
                                <thead>
                                    <tr>
                                        <th>Data/Hora</th>
                                        <th>Serviço</th>
                                        <th>Profissional</th>
                                        <th>Dependente</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAppointments.map(appt => (
                                        <tr key={appt.id} onClick={() => handleAppointmentClick(appt.id)} style={{ cursor: 'pointer' }}>
                                            <td>{new Date(appt.start_time).toLocaleString('pt-BR')}</td>
                                            <td>{appt.service_name}</td>
                                            <td>{appt.professional_name}</td>
                                            <td>{appt.dependent ? dependents.find(d => d.id === appt.dependent)?.name || '-' : '-'}</td>
                                            <td>{getStatusBadge(appt.status)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <p className="text-center text-muted my-4">Nenhum agendamento encontrado.</p>
                        )}
                    </Card.Body>
                </Card>
            )}

            <AppointmentModal
                show={showAppointmentModal}
                onHide={() => setShowAppointmentModal(false)}
                onSuccess={handleAppointmentSuccess}
                appointmentId={selectedAppointmentId}
                readOnlyDate={true}
            />
        </Container>
    );
};

export default ClientForm;
